import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from '../../../i18n/useTranslation';
import type { TranslationKey } from '../../../i18n/he';
import { routes } from '../../../Utils/routes';
import './Menu.css';

interface Item {
  to: string;
  label: TranslationKey;
  icon: string;
}

const ITEMS: readonly Item[] = [
  { to: routes.dashboard, label: 'navDashboard', icon: 'M4 20h16M7 20V10m5 10V4m5 16v-7' },
  { to: routes.calendar, label: 'navCalendar', icon: 'M7 3v2m10-2v2M4 8h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z' },
  { to: routes.payments, label: 'navPayments', icon: 'M6 3h12v18l-3-2-3 2-3-2-3 2V3zm3 5h6M9 12h6M9 16h4' },
  { to: routes.students, label: 'navStudents', icon: 'M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm8 1a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM3 20c.6-3.2 3.1-5 6-5s5.4 1.8 6 5m-.5 0c.4-2.4 2.1-3.8 4.3-3.8 1.3 0 2.4.5 3.2 1.4' },
  { to: routes.ensembles, label: 'navEnsembles', icon: 'M9 18V5l10-2v13M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm10-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0z' },
  { to: routes.lessons, label: 'navLessons', icon: 'M4 6h16M4 12h16M4 18h10' },
  { to: routes.profile, label: 'navProfile', icon: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm8.4-3a8.4 8.4 0 0 0-.1-1.2l2-1.6-2-3.4-2.4 1a8.3 8.3 0 0 0-2-1.2L15.5 3h-4l-.4 2.6a8.3 8.3 0 0 0-2 1.2l-2.4-1-2 3.4 2 1.6a8.4 8.4 0 0 0 0 2.4l-2 1.6 2 3.4 2.4-1c.6.5 1.3.9 2 1.2l.4 2.6h4l.4-2.6c.7-.3 1.4-.7 2-1.2l2.4 1 2-3.4-2-1.6c.1-.4.1-.8.1-1.2z' },
  { to: routes.backup, label: 'navBackup', icon: 'M6 18a4 4 0 0 1-1-7.9A6 6 0 0 1 17 9a4 4 0 0 1 1 7.9M12 12v8m0 0-3-3m3 3 3-3' },
  { to: routes.premiumArea, label: 'navPremium', icon: 'm12 3 2.5 5.5L20 9l-4 4 1 5.7L12 16l-5 2.7L8 13 4 9l5.5-.5L12 3z' }
];

const MOBILE_ITEMS = ITEMS.slice(0, 4);

function NavIcon({ path }: { path: string }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d={path} /></svg>;
}

export function Menu() {
  const { tr } = useTranslation();
  return (
    <>
      <nav className="desktop-app-menu" aria-label={tr('navDashboard')}>
        {ITEMS.slice(0, 4).map(item => <MenuLink key={item.to} item={item} />)}
        <Link className="desktop-quick-entry" to={`${routes.lessons}?add=1`}>
          <NavIcon path="M12 5v14M5 12h14" />
          <span>{tr('addLesson')}</span>
        </Link>
        {ITEMS.slice(4).map(item => <MenuLink key={item.to} item={item} />)}
      </nav>

      <nav className="mobile-app-menu" aria-label={tr('navDashboard')}>
        {MOBILE_ITEMS.slice(0, 2).map(item => <MenuLink key={item.to} item={item} compact />)}
        <Link className="mobile-quick-entry" to={`${routes.lessons}?add=1`} aria-label={tr('addLesson')}>
          <NavIcon path="M12 5v14M5 12h14" />
        </Link>
        {MOBILE_ITEMS.slice(2).map(item => <MenuLink key={item.to} item={item} compact />)}
      </nav>
    </>
  );
}

function MenuLink({ item, compact = false }: { item: Item; compact?: boolean }) {
  const { tr } = useTranslation();
  return (
    <NavLink aria-label={compact ? tr(item.label) : undefined} to={item.to} end={item.to === routes.dashboard} className={({ isActive }) => isActive ? 'app-menu-item is-active' : 'app-menu-item'}>
      <NavIcon path={item.icon} />
      {!compact && <span>{tr(item.label)}</span>}
    </NavLink>
  );
}
